import React from "react"
import PropTypes from "prop-types"
import styled from "styled-components"
import { Title, Spacer } from "@bw/bits"
import { Teaser } from "@bw/partials"
import { Checkbox, FormField, Select } from "@bw/forms"
import { Formik, Form, Field } from "formik"
import { useTranslation } from "react-i18next"
import { breakpoints } from "../../../theme"
import { toast } from "react-toastify"

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  @media (min-width: ${breakpoints.medium}px) {
    grid-template-columns: 1fr 1fr;
    gap: 25px;
  }
  @media (min-width: ${breakpoints.large}px) {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 25px;
  }
`

const initialValues = {
  canton: [],
  city: "all",
  title: "all",
}

let timer

const JobOffers = ({ count, contents, suptitle, title }) => {
  const { t } = useTranslation()
  const [filter, setFilter] = React.useState(initialValues)

  const cities = React.useMemo(() => {
    return contents.reduce((acc, job) => {
      if (job.city && !acc.includes(job.city)) {
        acc.push(job.city)
      }
      return acc;
    }, []).sort((a, b) => a.localeCompare(b))
  }, [contents])

  const postes = React.useMemo(() => {
    return contents.reduce((acc, job) => {
      for (let i = 0; i < job.type.length; i++) {
        if (job.type[i] && !acc.includes(job.type[i])) {
          acc.push(job.type[i])
        }
      }
      return acc
    }, []).sort((a, b) => a.localeCompare(b))
  }, [contents])

  const filteredJobs = React.useMemo(() => {
    let filtered = contents

    /**
     * Filter by canton
     */
    if (filter.canton.length > 0) {
      filtered = filtered.filter(job => filter.canton.includes(job.state))
    }

    /**
     * Filter by select city
     */
    if (filter.city !== "all") {
      filtered = filtered.filter(job => job.city === filter.city)
    }

    /**
     * Filter by select job title
     */
    if (filter.title !== "all") {
      filtered = filtered.filter(job => job.type.includes(filter.title))
    }

    if (filtered.length === 0) {
      toast.error(t("toast.noResult"))
    }

    return filtered
  }, [contents, filter, t])

  return (
    <Spacer gap="large">
      <Formik
        initialValues={initialValues}
        onSubmit={values => {
          setFilter({
            ...filter,
            ...values,
          })
        }}
      >
        {({ submitForm, handleReset }) => (
          <Form
            onChange={e => {
              if (e.target.name === "city") {
                clearTimeout(timer)
                timer = setTimeout(submitForm, 500)
              } else {
                submitForm()
              }
            }}
          >
            <Grid columns={count} responsive>
              <FormField title={t("form.canton")} name="canton">
                <Spacer direction="row">
                  {["be", "fr", "ge", "ju", "ne", "ti", "vd", "vs"].map(
                    state => (
                      <Field
                        key={state}
                        value={state}
                        name="canton"
                        component={Checkbox}
                        title={state.toUpperCase()}
                        checked={filter.canton.includes(state)}
                      />
                    )
                  )}
                </Spacer>
              </FormField>
              <FormField title={t("form.cityOnly")} name="city">
                <Field name="city" component={Select}>
                  <option value="all">{t("filterAll")}</option>
                  {cities.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Field>
              </FormField>
              <FormField title={t("form.poste")} name="poste">
                <Field name="title" component={Select}>
                  <option value="all">{t("filterAll")}</option>
                  {postes.map((item, i) => (
                    <option key={i} value={item}>
                      {item}
                    </option>
                  ))}
                </Field>
              </FormField>
            </Grid>
          </Form>
        )}
      </Formik>
      <Spacer responsive>
        <Title suptitle={suptitle} title={title} size="h3" />
        <Grid>
          {filteredJobs.map((item, i) => (
            <Teaser
              key={i}
              suptitle={item.suptitle}
              title={item.title}
              excerpt={item.excerpt}
              image={item.image}
              to={item.to}
            />
          ))}
        </Grid>
      </Spacer>
    </Spacer>
  )
}

JobOffers.propTypes = {
  suptitle: PropTypes.string,
  title: PropTypes.string,
  count: PropTypes.number,
  contents: PropTypes.arrayOf(
    PropTypes.shape({
      suptitle: PropTypes.string,
      title: PropTypes.string.isRequired,
      excerpt: PropTypes.string,
      image: PropTypes.element,
      to: PropTypes.string.isRequired,
    })
  ),
}

JobOffers.defaultProps = {
  count: 3,
}

export { JobOffers }
